import { useState } from 'react';
import api from '../../lib/api';
import { AuditChangeRow } from '../../components/admin/AuditChangeRow';
import type { AuditChange } from '../../types/admin';

const ENTITY_TYPES = ['Person', 'Organization', 'PersonSportTeam', 'User'] as const;

const inputCls =
  'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm';

export function AuditByEntitySubTab() {
  const [entityType, setEntityType] = useState<(typeof ENTITY_TYPES)[number]>('Person');
  const [entityId, setEntityId] = useState('');
  const [changes, setChanges] = useState<AuditChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function search() {
    if (!entityId.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ changes: AuditChange[] }>(
        `/api/audit/entities/${entityType}/${entityId}/recent`,
        { params: { limit: 50 } },
      );
      setChanges(res.data.changes);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2 items-end">
        <label className="flex flex-col text-xs text-gray-600 dark:text-gray-400">
          Entity type
          <select
            className={`${inputCls} w-44`}
            value={entityType}
            onChange={(e) => setEntityType(e.target.value as (typeof ENTITY_TYPES)[number])}
          >
            {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-gray-600 dark:text-gray-400">
          Entity ID
          <input
            className={`${inputCls} w-72`}
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
          />
        </label>
        <button
          onClick={search}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded"
        >
          Search
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}

      {changes === null ? (
        <div className="text-sm text-gray-500 dark:text-gray-400">Pick a type and enter an ID to see its history.</div>
      ) : changes.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-gray-500">No changes recorded for that entity.</div>
      ) : (
        <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {changes.map((c) => <AuditChangeRow key={c.id} change={c} />)}
        </div>
      )}
      {loading && <div className="text-xs text-gray-400 dark:text-gray-500">Loading…</div>}
    </div>
  );
}
